import { useLayoutEffect } from "react";
import { useMatches } from "react-router";
import { formatTitle } from "@common/hooks/useDocumentTitle";

/**
 * Shape of a route's `handle` for title purposes.
 * - `title`: static page title set centrally by TitleManager.
 * - `dynamicTitle`: the page sets its own title via useDocumentTitle() once its data loads.
 */
export type TitleHandle = { title?: string; dynamicTitle?: boolean };

function hasTitleHandle(handle: unknown): handle is TitleHandle {
    if (!handle || typeof handle !== "object") return false;
    const h = handle as TitleHandle;
    return h.title !== undefined || h.dynamicTitle === true;
}

/**
 * Render-nothing component mounted in the root layout route. Reads the deepest
 * matched route's title `handle` and sets document.title.
 *
 * Precedence:
 * - `handle.title` (string) -> set `<title> - HappyDoc`.
 * - no title handle          -> bare "HappyDoc" fallback.
 * - `handle.dynamicTitle`    -> neutralize to the bare product name. The page
 *   then sets its specific title via useDocumentTitle() once its data is ready.
 *   We must NOT leave the previous route's title in place: a slow lazy chunk, a
 *   permission/loading spinner, or a load error could otherwise strand another
 *   page's title (e.g. a different patient's name) in the tab.
 *
 * Uses useLayoutEffect so this runs before the page's passive useDocumentTitle
 * effect within the same commit (layout effects fire before passive effects).
 * That guarantees the page's specific title wins over this neutralize step,
 * independent of where TitleManager sits in the tree.
 */
export function TitleManager() {
    const matches = useMatches();

    useLayoutEffect(() => {
        const match = [...matches].reverse().find((m) => hasTitleHandle(m.handle));
        const handle = match?.handle as TitleHandle | undefined;

        document.title = handle?.dynamicTitle ? formatTitle() : formatTitle(handle?.title);
    }, [matches]);

    return null;
}
